import React, { useState, useEffect } from 'react';
import { Box, Heading, Text, VStack, Button, Progress, useToast, Flex, Spinner } from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useSelector } from 'react-redux';

const TakeLnaQuiz = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [quizFinished, setQuizFinished] = useState(false);
  const [finalResult, setFinalResult] = useState(null);
  const token = JSON.parse(localStorage.getItem('user'))?.token || "";
  const toast = useToast();

  useEffect(() => {
    fetchQuiz();
  }, [id]);

  const fetchQuiz = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/standalone-quiz/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuiz(res.data);
    } catch (err) {
      toast({ title: 'Error fetching quiz', status: 'error' });
      navigate('/home');
    }
  };

  const handleOptionSelect = (optionKey) => {
    // Save answer
    const currentQuestionId = quiz.questions[currentIndex]._id;
    const newAnswers = [...answers];
    const existingIndex = newAnswers.findIndex(a => a.questionId === currentQuestionId);
    
    if (existingIndex >= 0) {
      newAnswers[existingIndex] = { questionId: currentQuestionId, selectedOption: `Option ${optionKey}` };
    } else {
      newAnswers.push({ questionId: currentQuestionId, selectedOption: `Option ${optionKey}` });
    }
    setAnswers(newAnswers);

    // Go next or submit
    if (currentIndex < quiz.questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      submitQuiz(newAnswers);
    }
  };

  const submitQuiz = async (finalAnswers) => {
    setIsSubmitting(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/standalone-quiz/submit`, {
        quizId: quiz._id,
        answers: finalAnswers
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setFinalResult(res.data);
      setQuizFinished(true);
      toast({ title: 'Quiz Submitted!', description: 'Your LNA profile has been updated.', status: 'success' });
    } catch (err) {
      toast({ title: 'Failed to submit quiz', status: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!quiz) {
    return <Flex justify="center" p={20}><Spinner size="xl" /></Flex>;
  }

  if (quizFinished && finalResult) {
    navigate(`/lna-quiz/${quiz._id}/result`);
    return null;
  }

  const currentQuestion = quiz.questions[currentIndex];
  const progressPercent = ((currentIndex) / quiz.questions.length) * 100;

  return (
    <Box maxW="800px" mx="auto" p={6} mt={10}>
      <Flex justify="space-between" mb={2}>
        <Text fontWeight="800" color="gray.400">Question {currentIndex + 1} of {quiz.questions.length}</Text>
        <Text fontWeight="800" color="gray.400">{quiz.title}</Text>
      </Flex>
      <Progress value={progressPercent} size="sm" colorScheme="purple" mb={8} borderRadius="full" />

      <Box bg="var(--bg-surface)" p={8} borderRadius="3xl" boxShadow="lg">
        <Text fontSize="xs" fontWeight="800" color="purple.400" mb={2} textTransform="uppercase">
          {currentQuestion.topic}
        </Text>
        <Heading size="lg" mb={8} lineHeight="1.4">
          {currentQuestion.questionText}
        </Heading>

        <VStack spacing={4} align="stretch">
          {['A', 'B', 'C', 'D'].map(key => (
            <Button
              key={key}
              size="lg"
              h="auto"
              py={4}
              px={6}
              justifyContent="flex-start"
              textAlign="left"
              whiteSpace="normal"
              bg="whiteAlpha.100"
              _hover={{ bg: "purple.500", color: "white" }}
              onClick={() => handleOptionSelect(key)}
              isDisabled={isSubmitting}
            >
              <Text fontWeight="bold" mr={4} opacity={0.6}>{key}.</Text>
              <Text>{currentQuestion.options[key]}</Text>
            </Button>
          ))}
        </VStack>
      </Box>
    </Box>
  );
};

export default TakeLnaQuiz;
